import { Helmet } from "react-helmet";
import { useState, useMemo } from "react";
import {
  FaPlus,
  FaMagnifyingGlass,
  FaTicket,
  FaChevronLeft,
  FaChevronRight,
  FaAnglesLeft,
  FaAnglesRight,
  FaFilter,
  FaUser,
  FaClock,
  FaHashtag,
  FaPenToSquare,
  FaTrashCan,
} from "react-icons/fa6";
import { BtnDefault } from "../../../components/buttons/BtnDefault";
import { Header } from "../../../components/Header";
import { DashboardLayout } from "../../../components/layouts/DashboardLayout";
import { useHookTicket } from "../../../hooks/useHookTicket";
import { TicketCreate } from "./TicketCreate";
import { formatTimeWIB } from "../../../utilities/formatTimeWIB";
import { formatDate } from "../../../utilities/formatDate";
import { useNavigate } from "react-router-dom";
import { TicketUpdate } from "./TicketUpdate";
import { TicketDelete } from "./TicketDelete";

/* ============================================
   KOMPONEN: Skeleton
   ============================================ */
const SkeletonRow = ({ cols = 8 }) => (
  <tr className="animate-pulse">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div
          className="h-3.5 bg-slate-700/60 rounded-md"
          style={{ width: `${40 + Math.random() * 50}%` }}
        />
      </td>
    ))}
  </tr>
);

const SkeletonCard = () => (
  <div
    className="rounded-2xl p-5 animate-pulse"
    style={{
      background: "rgba(15, 23, 42, 0.5)",
      border: "1px solid rgba(51, 65, 85, 0.3)",
    }}
  >
    <div className="flex justify-between items-start mb-4">
      <div className="space-y-2">
        <div className="h-4 bg-slate-700/60 rounded-md w-32" />
        <div className="h-3 bg-slate-700/40 rounded-md w-24" />
      </div>
      <div className="h-6 bg-slate-700/40 rounded-full w-16" />
    </div>
    <div
      className="space-y-2 pt-3"
      style={{ borderTop: "1px solid rgba(51, 65, 85, 0.2)" }}
    >
      <div className="h-3 bg-slate-700/50 rounded w-full" />
      <div className="h-3 bg-slate-700/30 rounded w-3/4" />
    </div>
    <div
      className="flex justify-between items-center pt-3"
      style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}
    >
      <div className="h-3 bg-slate-700/30 rounded w-24" />
      <div className="flex gap-2">
        <div className="w-8 h-8 bg-slate-700/40 rounded-lg" />
        <div className="w-8 h-8 bg-slate-700/40 rounded-lg" />
      </div>
    </div>
  </div>
);

/* ============================================
   KOMPONEN: Status Badge
   ============================================ */
const StatusBadge = ({ status }) => {
  const config = {
    open: {
      bg: "bg-red-500/12",
      text: "text-red-400",
      border: "border-red-500/20",
      dot: "bg-red-400",
    },
    process: {
      bg: "bg-amber-500/12",
      text: "text-amber-400",
      border: "border-amber-500/20",
      dot: "bg-amber-400",
    },
    pending: {
      bg: "bg-amber-500/12",
      text: "text-amber-400",
      border: "border-amber-500/20",
      dot: "bg-amber-400",
    },
    closed: {
      bg: "bg-emerald-500/12",
      text: "text-emerald-400",
      border: "border-emerald-500/20",
      dot: "bg-emerald-400",
    },
    resolved: {
      bg: "bg-emerald-500/12",
      text: "text-emerald-400",
      border: "border-emerald-500/20",
      dot: "bg-emerald-400",
    },
    done: {
      bg: "bg-emerald-500/12",
      text: "text-emerald-400",
      border: "border-emerald-500/20",
      dot: "bg-emerald-400",
    },
  };

  const c = config[status?.toLowerCase()] || config.open;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide ${c.bg} ${c.text} border ${c.border}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {status || "-"}
    </span>
  );
};

/* ============================================
   KOMPONEN: Ticket
   ============================================ */
export const Ticket = () => {
  const navigate = useNavigate();
  const [showSidebar, setShowSidebar] = useState(false);
  const [openModalCreate, setOpenModalCreate] = useState(false);
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const [selectedData, setSelectedData] = useState(null);

  /* ---------- Filter & Search (client-side) ---------- */
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  /* ---------- Delete State ---------- */
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [targetDeleteId, setTargetDeleteId] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);

  /* ---------- Hook ---------- */
  const {
    data,
    meta,
    loading,
    error,
    refreshData,
    keyword,
    setKeyword,
    nasId,
    setNasId,
    popId,
    setPopId,
    odpId,
    setOdpId,
    page,
    setPage,
    limit,
    setLimit,
    sort,
    setSort,
  } = useHookTicket();

  /* ---------- Client-side Filter ---------- */
  const filteredData = useMemo(() => {
    if (!data) return [];
    return data.filter((item) => {
      const matchesSearch =
        item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.pppoe_user?.name?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        filterCategory === "" || item?.category?.name === filterCategory;
      const matchesStatus =
        filterStatus === "" || item?.status?.name === filterStatus;

      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [data, searchTerm, filterCategory, filterStatus]);

  /* ---------- Extract unique filters ---------- */
  const categories = useMemo(
    () => [...new Set(data?.map((i) => i?.category?.name).filter(Boolean))],
    [data],
  );
  const statuses = useMemo(
    () => [...new Set(data?.map((i) => i?.status?.name).filter(Boolean))],
    [data],
  );

  /* ---------- Pagination Logic ---------- */
  const getPages = () => {
    if (!meta) return [];
    const total = meta.last_page;
    const current = meta.current_page;
    const delta = 2;
    const pages = [];
    const left = Math.max(2, current - delta);
    const right = Math.min(total - 1, current + delta);
    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < total - 1) pages.push("...");
    if (total > 1) pages.push(total);
    return pages;
  };

  /* ---------- Handlers ---------- */
  const handleEdit = (item) => {
    setSelectedData(item);
    setOpenModalEdit(true);
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    setLoadingDelete(true);
    try {
      // await TicketService.delete({ id: targetDeleteId });
      refreshData();
      setOpenDeleteConfirmation(false);
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingDelete(false);
    }
  };

  /* ---------- Column Config ---------- */
  const columns = [
    { key: "no", label: "No", align: "center", width: "60px" },
    { key: "action", label: "Aksi", align: "center", width: "100px" },
    { key: "code", label: "Kode Tiket", align: "left" },
    { key: "status", label: "Status", align: "left" },
    // { key: "datetime", label: "Tanggal Dibuat", align: "left" },
    { key: "customer", label: "Pelanggan", align: "left" },
    { key: "issue", label: "Jenis Gangguan", align: "left" },
    { key: "schedule", label: "Jadwal Kunjungan", align: "left" },
    { key: "description", label: "Keterangan", align: "left" },
  ];

  return (
    <>
      <Helmet>
        <title>Tiket Gangguan - DANELA NET</title>
        <meta
          name="description"
          content="Kelola tiket gangguan pelanggan, pantau status penanganan, dan jadwalkan kunjungan teknisi."
        />
      </Helmet>

      <DashboardLayout
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
      >
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex items-center gap-3">
            <h2 className="text-base md:text-xl font-bold text-gray-100">
              Tiket Gangguan
            </h2>
            {meta && (
              <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                {meta.total} tiket
              </span>
            )}
          </div>
          <BtnDefault onClick={() => setOpenModalCreate(true)}>
            <span className="hidden lg:flex items-center gap-2">
              <FaPlus className="text-xs" />
              Buat Tiket
            </span>
            <FaPlus className="lg:hidden" />
          </BtnDefault>
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-5">
          {/* ===== Search & Filter ===== */}
          <section
            className="rounded-2xl p-4 sm:p-5"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
              backdropFilter: "blur(12px)",
            }}
            aria-label="Filter dan pencarian"
          >
            <div className="flex flex-col lg:flex-row lg:items-end gap-4">
              {/* Search */}
              <div className="w-full lg:max-w-xs flex-shrink-0">
                <label
                  htmlFor="search-ticket"
                  className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider"
                >
                  Cari Tiket
                </label>
                <div className="relative">
                  <FaMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm" />
                  <input
                    id="search-ticket"
                    type="text"
                    placeholder="Kode tiket, pelanggan..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                  />
                </div>
              </div>

              {/* Filters */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1.5">
                  <FaFilter className="text-xs text-slate-500" />
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Filter
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <select
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all appearance-none cursor-pointer"
                    >
                      <option value="" className="bg-slate-800">
                        Semua Kategori
                      </option>
                      {categories.map((c) => (
                        <option key={c} value={c} className="bg-slate-800">
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all appearance-none cursor-pointer"
                    >
                      <option value="" className="bg-slate-800">
                        Semua Status
                      </option>
                      {statuses.map((s) => (
                        <option key={s} value={s} className="bg-slate-800">
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ===== Desktop Table ===== */}
          <section
            className="hidden lg:block rounded-2xl overflow-hidden"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
              backdropFilter: "blur(12px)",
            }}
            aria-label="Tabel tiket gangguan"
          >
            <div className="overflow-x-auto">
              <table className="min-w-max max-w-screen-2xl text-sm">
                {/* Head */}
                <thead>
                  <tr
                    className="border-b"
                    style={{
                      background: "rgba(15, 23, 42, 0.7)",
                      borderColor: "rgba(51, 65, 85, 0.3)",
                    }}
                  >
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        className={`px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400 ${
                          col.align === "center" ? "text-center" : "text-left"
                        }`}
                        style={col.width ? { width: col.width } : undefined}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                {/* Body */}
                <tbody
                  className="divide-y"
                  style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}
                >
                  {/* Loading */}
                  {loading && !data && (
                    <>
                      {[...Array(5)].map((_, i) => (
                        <SkeletonRow key={i} cols={columns.length} />
                      ))}
                    </>
                  )}

                  {/* Empty */}
                  {!loading && filteredData?.length === 0 && (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className="px-4 py-16 text-center"
                      >
                        <div className="flex flex-col items-center gap-3">
                          <div
                            className="w-16 h-16 rounded-2xl flex items-center justify-center"
                            style={{
                              background: "rgba(51, 65, 85, 0.2)",
                              border: "1px solid rgba(51, 65, 85, 0.2)",
                            }}
                          >
                            <FaTicket className="text-2xl text-slate-600" />
                          </div>
                          <div>
                            <p className="text-slate-400 font-medium">
                              Tidak ada tiket ditemukan
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {searchTerm || filterCategory || filterStatus
                                ? "Coba ubah filter atau kata kunci pencarian"
                                : "Belum ada tiket gangguan yang terdaftar"}
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Data Rows */}
                  {!loading &&
                    filteredData?.map((item, index) => (
                      <tr
                        key={item.id}
                        className="transition-colors duration-150 cursor-pointer"
                        style={{ background: "transparent" }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            "rgba(0, 180, 216, 0.04)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                        }}
                        onClick={() =>
                          navigate(`/dashboard/ticket/detail/${item.id}`)
                        }
                      >
                        {/* No */}
                        <td className="px-4 py-3 text-center text-slate-500 font-mono text-xs">
                          {meta?.from ? meta.from + index : index + 1}
                        </td>

                        {/* Action */}
                        <td className="px-4 py-3">
                          <div className="flex justify-center gap-1.5">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(item);
                              }}
                              className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 transition-all"
                              title="Edit tiket"
                            >
                              <FaPenToSquare className="text-sm" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setTargetDeleteId(item.id);
                                setOpenDeleteConfirmation(true);
                              }}
                              className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-slate-700/40 transition-all"
                              title="Hapus tiket"
                            >
                              <FaTrashCan className="text-sm" />
                            </button>
                          </div>
                        </td>

                        {/* Tanggal Dibuat */}
                        {/* <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-slate-400">
                            <FaClock className="text-xs text-slate-500" />
                            <span className="text-xs whitespace-nowrap">
                              {item?.created_at && `${formatDate(item?.created_at)} ${formatTimeWIB(item?.created_at)}`}
                            </span>
                          </div>
                        </td> */}

                        {/* Code */}
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs text-cyan-400/80 bg-cyan-500/8 px-2 py-0.5 rounded">
                            {item?.code || "-"}
                          </span>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3">
                          <StatusBadge status={item?.status?.name} />
                        </td>

                        {/* Customer */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FaUser className="text-xs text-slate-500" />
                            <span className="text-gray-100 font-medium">
                              {item?.pppoe_user?.name || "-"}
                            </span>
                          </div>
                        </td>

                        {/* Issue */}
                        <td className="px-4 py-3 text-slate-300 max-w-[200px]">
                          <span className="truncate block">
                            {item?.title || "-"}
                          </span>
                        </td>

                        {/* Schedule */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-slate-400">
                            <FaClock className="text-xs text-slate-500" />
                            <span className="text-xs whitespace-nowrap">
                              {item?.schedule?.start_time &&
                                `${formatDate(item?.schedule?.start_time)} ${formatTimeWIB(item?.schedule?.start_time)}`}
                            </span>
                          </div>
                        </td>

                        {/* Description */}
                        <td className="px-4 py-3 text-slate-400 text-xs max-w-[220px]">
                          <p className="truncate">{item?.description || "-"}</p>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {meta && meta.last_page > 1 && (
              <div
                className="flex items-center justify-between px-5 py-3.5"
                style={{ borderTop: "1px solid rgba(51, 65, 85, 0.2)" }}
              >
                <p className="text-xs text-slate-500">
                  Menampilkan{" "}
                  <span className="text-slate-300 font-medium">
                    {meta.from}
                  </span>{" "}
                  sampai{" "}
                  <span className="text-slate-300 font-medium">{meta.to}</span>{" "}
                  dari{" "}
                  <span className="text-slate-300 font-medium">
                    {meta.total}
                  </span>
                </p>

                <nav
                  className="flex items-center gap-1"
                  aria-label="Navigasi halaman"
                >
                  <button
                    onClick={() => setPage(1)}
                    disabled={meta.current_page === 1}
                    className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    aria-label="Halaman pertama"
                  >
                    <FaAnglesLeft className="text-xs" />
                  </button>
                  <button
                    onClick={() => setPage(meta.current_page - 1)}
                    disabled={meta.current_page === 1}
                    className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    aria-label="Halaman sebelumnya"
                  >
                    <FaChevronLeft className="text-xs" />
                  </button>

                  {getPages().map((p, i) =>
                    p === "..." ? (
                      <span
                        key={`dot-${i}`}
                        className="px-2 text-slate-600 text-sm"
                      >
                        ···
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`min-w-[34px] h-9 px-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          meta.current_page === p
                            ? "bg-cyan-600 text-white shadow-lg shadow-cyan-600/25"
                            : "text-slate-400 hover:text-gray-100 hover:bg-slate-700/40"
                        }`}
                      >
                        {p}
                      </button>
                    ),
                  )}

                  <button
                    onClick={() => setPage(meta.current_page + 1)}
                    disabled={meta.current_page === meta.last_page}
                    className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    aria-label="Halaman berikutnya"
                  >
                    <FaChevronRight className="text-xs" />
                  </button>
                  <button
                    onClick={() => setPage(meta.last_page)}
                    disabled={meta.current_page === meta.last_page}
                    className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    aria-label="Halaman terakhir"
                  >
                    <FaAnglesRight className="text-xs" />
                  </button>
                </nav>
              </div>
            )}
          </section>

          {/* ===== Mobile Card List ===== */}
          <section
            className="lg:hidden space-y-3"
            aria-label="Daftar tiket gangguan"
          >
            {/* Loading */}
            {loading && !data && (
              <>
                {[...Array(4)].map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </>
            )}

            {/* Empty */}
            {!loading && filteredData?.length === 0 && (
              <div
                className="rounded-2xl p-12 text-center"
                style={{
                  background: "rgba(15, 23, 42, 0.5)",
                  border: "1px solid rgba(51, 65, 85, 0.3)",
                }}
              >
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{
                    background: "rgba(51, 65, 85, 0.2)",
                    border: "1px solid rgba(51, 65, 85, 0.2)",
                  }}
                >
                  <FaTicket className="text-2xl text-slate-600" />
                </div>
                <p className="text-slate-400 font-medium">
                  Tidak ada tiket ditemukan
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {searchTerm || filterCategory || filterStatus
                    ? "Coba ubah filter pencarian"
                    : "Belum ada tiket gangguan"}
                </p>
              </div>
            )}

            {/* Cards */}
            {!loading &&
              filteredData?.map((item) => (
                <article
                  key={item.id}
                  className="rounded-2xl overflow-hidden transition-all duration-200"
                  style={{
                    background: "rgba(15, 23, 42, 0.5)",
                    border: "1px solid rgba(51, 65, 85, 0.3)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor =
                      "rgba(0, 180, 216, 0.2)";
                    e.currentTarget.style.background = "rgba(15, 23, 42, 0.7)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "rgba(51, 65, 85, 0.3)";
                    e.currentTarget.style.background = "rgba(15, 23, 42, 0.5)";
                  }}
                >
                  {/* Header Card */}
                  <div
                    className="px-4 py-3 flex justify-between items-center"
                    style={{
                      background: "rgba(15, 23, 42, 0.7)",
                      borderBottom: "1px solid rgba(51, 65, 85, 0.2)",
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="p-1.5 rounded-lg"
                        style={{
                          background: "rgba(0, 180, 216, 0.1)",
                          border: "1px solid rgba(0, 180, 216, 0.15)",
                        }}
                      >
                        <FaHashtag className="text-xs text-cyan-400" />
                      </div>
                      <span className="text-xs font-bold text-slate-300 font-mono">
                        {item?.code || "-"}
                      </span>
                    </div>
                    <StatusBadge status={item?.status?.name} />
                  </div>

                  {/* Konten Card */}
                  <div className="p-4">
                    <div className="mb-3">
                      <h3 className="font-semibold text-gray-100 leading-tight text-sm">
                        {item?.title || "Tidak ada judul"}
                      </h3>
                      <div className="flex items-center gap-1.5 text-slate-500 mt-1">
                        <FaUser className="text-[11px]" />
                        <span className="text-xs font-medium">
                          {item?.pppoe_user?.name || "Tidak ada pelanggan"}
                        </span>
                      </div>
                    </div>

                    <p
                      className="text-xs text-slate-400 bg-slate-800/40 p-2.5 rounded-lg line-clamp-2 italic mb-3"
                      style={{ border: "1px solid rgba(51, 65, 85, 0.15)" }}
                    >
                      &ldquo;{item?.description || "Tidak ada keterangan"}
                      &rdquo;
                    </p>

                    <div
                      className="flex items-center justify-between pt-3"
                      style={{
                        borderTop: "1px solid rgba(51, 65, 85, 0.15)",
                      }}
                    >
                      <div className="flex items-center gap-1.5 text-slate-500">
                        <FaClock className="text-[11px]" />
                        <span className="text-[11px] font-medium">
                          {item?.created_at || "-"}
                        </span>
                      </div>
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-2.5 rounded-xl text-slate-400 hover:text-cyan-400 transition-all"
                          style={{
                            background: "rgba(0, 180, 216, 0.06)",
                            border: "1px solid rgba(0, 180, 216, 0.1)",
                          }}
                        >
                          <FaPenToSquare className="text-sm" />
                        </button>
                        <button
                          onClick={() => {
                            setTargetDeleteId(item.id);
                            setOpenDeleteConfirmation(true);
                          }}
                          className="p-2.5 rounded-xl text-slate-400 hover:text-red-400 transition-all"
                          style={{
                            background: "rgba(239, 68, 68, 0.06)",
                            border: "1px solid rgba(239, 68, 68, 0.1)",
                          }}
                        >
                          <FaTrashCan className="text-sm" />
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              ))}

            {/* Mobile Pagination */}
            {meta && meta.last_page > 1 && (
              <div
                className="flex items-center justify-between pt-3 pb-6 px-1"
                style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}
              >
                <button
                  onClick={() => setPage(meta.current_page - 1)}
                  disabled={meta.current_page === 1}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  style={{
                    background: "rgba(15, 23, 42, 0.5)",
                    border: "1px solid rgba(51, 65, 85, 0.3)",
                  }}
                >
                  <FaChevronLeft className="text-xs" />
                  Sebelumnya
                </button>

                <span className="text-sm font-medium text-slate-400">
                  <span className="text-cyan-400">{meta.current_page}</span>
                  <span className="text-slate-600 mx-1">/</span>
                  {meta.last_page}
                </span>

                <button
                  onClick={() => setPage(meta.current_page + 1)}
                  disabled={meta.current_page === meta.last_page}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  style={{
                    background: "rgba(15, 23, 42, 0.5)",
                    border: "1px solid rgba(51, 65, 85, 0.3)",
                  }}
                >
                  Berikutnya
                  <FaChevronRight className="text-xs" />
                </button>
              </div>
            )}
          </section>
        </main>
      </DashboardLayout>

      {/* ===== Modals ===== */}
      {openModalCreate && (
        <TicketCreate
          open={openModalCreate}
          setOpen={setOpenModalCreate}
          refreshData={refreshData}
        />
      )}

      {/* --- Tambahkan Bagian Ini --- */}
      {openModalEdit && selectedData && (
        <TicketUpdate
          open={openModalEdit}
          setOpen={setOpenModalEdit}
          refreshData={refreshData}
          initialData={selectedData}
        />
      )}

      {openDeleteConfirmation && targetDeleteId && (
        <TicketDelete
          open={openDeleteConfirmation}
          setOpen={setOpenDeleteConfirmation}
          refreshData={refreshData}
          targetId={targetDeleteId}
        />
      )}
    </>
  );
};
